import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import axios from "@/lib/axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  FileAudio, Upload, Check, X, AlertTriangle, Download,
  Clock, ArrowRight, ArrowLeft, Megaphone, Trash2, Loader2,
  History as HistoryIcon, UserCircle, Save, Settings, Target, Radio, Paperclip, File, MessageSquare, RefreshCw
} from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { CustomAudioPlayer } from "@/components/audio/CustomAudioPlayer";

// Type Definitions

interface Device {
  _id: string;
  name: string;
  isOnline?: boolean;
}

interface Group {
  _id: string;
  name: string;
}

interface AudioStatusHistory {
  _id: string;
  from: string;
  to: string;
  note?: string;
  date: string;
  user: { _id: string; name: string; email: string; role: string };
}

interface AudioFile {
  _id: string;
  url: string;
  fileName: string;
  status: string;
  statusHistory?: AudioStatusHistory[];
}

interface StatusHistory {
  _id: string;
  from: string;
  to: string;
  note?: string;
  date: string;
  user: { _id: string; name: string; email: string; role: string };
}

interface RevisionNote {
  note: string;
  user: { id: string; name: string; role: string; company: string };
  date: string;
}

interface Announcement {
  _id: string;
  title: string;
  content: string;
  scheduleType: string;
  songInterval?: number;
  minuteInterval?: number;
  startDate: string;
  endDate: string;
  status: string;
  audioFile: AudioFile[] | AudioFile | string;
  attachments: any[];
  targetDevices: Device[];
  targetGroups: Group[];
  statusHistory: StatusHistory[];
  revisionNotes?: RevisionNote[];
  immediateInterrupt: boolean;
  createdAt: string;
  updatedAt: string;
  notes?: string;
  createdBy: { name: string; role: string };
}

const statusColors = {
  initial_review: "bg-blue-100 text-blue-800 border-blue-300",
  pending_admin: "bg-purple-100 text-purple-800 border-purple-300",
  manager_review: "bg-yellow-100 text-yellow-800 border-yellow-300",
  approved: "bg-green-100 text-green-800 border-green-300",
  rejected: "bg-red-100 text-red-800 border-red-300"
} as const;

const statusDetailLabels = {
  initial_review: "Veeq Seslendirme Bekleniyor",
  pending_admin: "Revize Talebi",
  manager_review: "İşlem Onayı Bekliyor",
  approved: "Yayında",
  rejected: "İptal Edildi"
} as const;

type StatusType = keyof typeof statusColors;

// Bir ses dosyası müşteri onayı bekliyorsa ya da revizyon isteniyorsa, anonsun
// kendi "status" alanı bunu senkron yansıtmayabilir (bkz. updateAnnouncementStatus
// akışı) - bu yüzden üst rozet her zaman ses dosyalarının gerçek durumuna göre
// hesaplanmalı, sadece announcement.status'e bakılmamalı
const getEffectiveStatusKey = (status: string, audioFile?: AudioFile[] | AudioFile | string): StatusType => {
  if (status === "rejected" || status === "approved") {
    return status as StatusType;
  }
  const files = Array.isArray(audioFile) ? audioFile : [];
  if (files.some((audio) => audio.status === "manager_review")) {
    return "manager_review";
  }
  if (files.some((audio) => audio.status === "pending_admin")) {
    return "pending_admin";
  }
  return status as StatusType;
};

// "Revize Talebi" (pending_admin) iki farklı anlama gelebilir: ya super admin
// müşteriye az önce bir not iletti (müşterinin cevabı bekleniyor), ya da
// müşteri başlık/içeriğini güncelleyip geri gönderdi (admin'in incelemesi
// bekleniyor). Anonsun kendi statusHistory'sindeki en son pending_admin
// geçişini kimin yaptığına bakarak ikisini ayırt et
const getEffectiveStatusLabel = (status: string, audioFile: AudioFile[] | AudioFile | string | undefined, statusHistory: StatusHistory[] | undefined): string => {
  const key = getEffectiveStatusKey(status, audioFile);
  if (key === "pending_admin" && status === "pending_admin" && Array.isArray(statusHistory)) {
    const entries = statusHistory.filter((h) => h.to === "pending_admin");
    if (entries.length > 0) {
      const latest = entries.reduce((a, b) => (new Date(a.date) > new Date(b.date) ? a : b));
      if (latest.user?.role === "super_admin") {
        return "Müşteri Bekleniyor";
      }
    }
  }
  return statusDetailLabels[key];
};

const MAX_QUICK_NOTES = 5;

const QUICK_NOTE_OPTIONS = [
  "Ekte dosya bulunmamaktadır.",
  "Ekteki dosya kalitesi düşük, tutarlar okunmuyor.",
  "Ürün ve tutarları yazılı iletmeniz uygun olacaktır."
];

const hasAudioFiles = (audioFile: string | AudioFile | AudioFile[] | undefined): boolean => {
  if (!audioFile) return false;
  if (Array.isArray(audioFile)) return audioFile.length > 0;
  return true;
};

// Tarih formatlama fonksiyonu
const formatShortDate = (date: string | Date | undefined) => {
  if (!date) return '-';
  const d = new Date(date);
  return new Intl.DateTimeFormat('tr-TR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(d);
};

export default function AnnouncementDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<Partial<Announcement>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [replacingAudioId, setReplacingAudioId] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDeletingAll, setIsDeletingAll] = useState(false);
  const [selectedQuickNote, setSelectedQuickNote] = useState<string | null>(null);
  const [isSendingQuickNote, setIsSendingQuickNote] = useState(false);
  const [isStatusUpdating, setIsStatusUpdating] = useState(false);
  const pageTopRef = useRef<HTMLDivElement>(null);
  const audioSectionRef = useRef<HTMLDivElement>(null);

  const { data: announcement, isLoading } = useQuery<Announcement, Error>({
    queryKey: ["announcement", id],
    queryFn: async () => {
      const { data } = await axios.get<Announcement>(`/announcements/${id}`);
      return data;
    },
    enabled: !!id,
    staleTime: 0,
  });

  // Sayfa açıldığında en üste scroll
  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    // Parent container'lar için de scroll
    pageTopRef.current?.scrollIntoView({ behavior: 'instant', block: 'start' });
  }, [id, announcement]);

  // Backend'den gelen UTC tarihi local time'a çevir
  const formatDateForInput = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    if (type === 'datetime-local') {
      if (!value) {
        setFormData(prev => ({ ...prev, [name]: undefined }));
        return;
      }
      try {
        const localDate = new Date(value);
        if (isNaN(localDate.getTime())) return;
        const isoString = localDate.toISOString();
        setFormData(prev => ({ ...prev, [name]: isoString }));
      } catch (error) {
        console.error('Tarih dönüşüm hatası:', error);
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'number' ? Number(value) : value
      }));
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, immediateInterrupt: e.target.checked }));
  };

  const handleScheduleTypeChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      scheduleType: value,
      songInterval: value === 'songs' ? prev.songInterval : undefined,
      minuteInterval: value === 'minutes' ? prev.minuteInterval : undefined
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSaving) return;

    try {
      setIsSaving(true);
      const toastId = toast.loading("Değişiklikler kaydediliyor...");

      const updatedFormData: Record<string, any> = {
        title: (formData.title ?? announcement?.title) || '',
        content: (formData.content ?? announcement?.content) || '',
        scheduleType: formData.scheduleType ?? announcement?.scheduleType,
        startDate: formData.startDate ?? announcement?.startDate,
        endDate: formData.endDate ?? announcement?.endDate,
        immediateInterrupt: String(formData.immediateInterrupt ?? announcement?.immediateInterrupt ?? false),
        targetDevices: JSON.stringify(announcement?.targetDevices || []),
        targetGroups: JSON.stringify(announcement?.targetGroups || [])
      };

      if (updatedFormData.scheduleType === 'songs') {
        updatedFormData.songInterval = formData.songInterval?.toString() ?? announcement?.songInterval?.toString();
      } else if (updatedFormData.scheduleType === 'minutes') {
        updatedFormData.minuteInterval = formData.minuteInterval?.toString() ?? announcement?.minuteInterval?.toString();
      }

      await axios.put(`/announcements/${id}`, updatedFormData);
      toast.success("Anons başarıyla güncellendi", { id: toastId });
      queryClient.invalidateQueries({ queryKey: ["announcement", id] });
      navigate("/announcements");
    } catch (error: unknown) {
      if (error instanceof Error) {
        toast.error((error as any).response?.data?.message || error.message || "Anons güncellenirken bir hata oluştu");
      } else {
        toast.error("Anons güncellenirken bir hata oluştu");
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleError = (error: unknown, defaultMessage: string) => {
    console.error(defaultMessage, error);
    if (error instanceof Error) {
      toast.error((error as any).response?.data?.message || error.message || defaultMessage);
    } else {
      toast.error(defaultMessage);
    }
  };

  const handleAudioUpload = async (files: FileList | File[]) => {
    try {
      setIsUploading(true);
      setUploadProgress(0);
      const fileArray = Array.from(files);
      const totalFiles = fileArray.length;

      for (let i = 0; i < fileArray.length; i++) {
        const file = fileArray[i];
        const formData = new FormData();
        formData.append('audio', file);
        await axios.post(`/announcements/${id}/audio`, formData, {
          headers: { 'Content-Type': undefined },
          onUploadProgress: (progressEvent) => {
            const fileProgress = progressEvent.total
              ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
              : 0;
            const overallProgress = Math.round(((i * 100) + fileProgress) / totalFiles);
            setUploadProgress(overallProgress);
          }
        });
      }
      setUploadProgress(100);
      await axios.put(`/announcements/${id}/audio/all/status`, {
        status: 'manager_review',
        note: `Ses dosyası yüklendi`,
        updateAnnouncementStatus: false
      });
      // Veri yenilenene kadar bekle, sonra uploading'i kapat
      await queryClient.refetchQueries({ queryKey: ["announcement", id] });
      // Scroll pozisyonunu koru - ses dosyaları bölümünde kal
      setTimeout(() => {
        audioSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
      toast.success(files.length > 1 ? `${files.length} ses dosyası başarıyla yüklendi` : "Ses dosyası başarıyla yüklendi");
    } catch (error) {
      handleError(error, "Ses dosyası yüklenirken bir hata oluştu");
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleStatusChange = async (status: string) => {
    if (isStatusUpdating) return;
    try {
      setIsStatusUpdating(true);
      await axios.put(`/announcements/${id}/audio/all/status`, {
        status,
        note: status === 'manager_review' ? 'Manager incelemesine gönderildi' : `Durum ${status} olarak güncellendi`
      });
      queryClient.invalidateQueries({ queryKey: ["announcement", id] });
      toast.success("Durum başarıyla güncellendi");
      navigate("/announcements");
    } catch (error) {
      handleError(error, "Durum güncellenirken bir hata oluştu");
    } finally {
      setIsStatusUpdating(false);
    }
  };

  // Seçili hazır notu müşteriye ilet - anonsu Revize Talebi durumuna çeker,
  // müşteri panelinde bu not "Düzeltme Bekliyor" yazısının altında görünür.
  // Sayfadan çıkılmaz - admin art arda farklı notlar seçip gönderebilsin diye
  const handleSendQuickNote = async () => {
    if (!selectedQuickNote || isSendingQuickNote) return;
    try {
      setIsSendingQuickNote(true);
      await axios.put(`/announcements/${id}/audio/all/status`, {
        status: 'pending_admin',
        note: selectedQuickNote
      });
      await queryClient.invalidateQueries({ queryKey: ["announcement", id] });
      toast.success("Not müşteriye iletildi");
      setSelectedQuickNote(null);
    } catch (error) {
      handleError(error, "Not iletilirken bir hata oluştu");
    } finally {
      setIsSendingQuickNote(false);
    }
  };

  const handleReplaceAudio = async (
    e: React.ChangeEvent<HTMLInputElement>,
    audioId: string
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsUploading(true);
      setReplacingAudioId(audioId);
      const toastId = toast.loading("Ses dosyası değiştiriliyor...");

      // 1. Eski dosyayı sil
      await axios.delete(`/announcements/${id}/audio/${audioId}`);

      // 2. Yeni dosyayı yükle
      const formData = new FormData();
      formData.append('audio', file);
      const uploadResponse = await axios.post(`/announcements/${id}/audio`, formData, {
        headers: { 'Content-Type': undefined },
      });

      // 3. Sadece yeni yüklenen dosyanın durumunu güncelle
      // (Not: /audio/all/status TÜM ses dosyalarını etkiler - burada kullanılmamalı,
      // aksi halde dokunulmamış diğer "Revize Bekleniyor" dosyaları da
      // sessizce "İncelemede" durumuna geçer ve revize ikonlarını kaybeder.)
      const updatedAudioFiles: AudioFile[] = uploadResponse.data?.audioFile || [];
      const newAudioFile = updatedAudioFiles[updatedAudioFiles.length - 1];
      if (newAudioFile?._id) {
        await axios.put(`/announcements/${id}/audio/${newAudioFile._id}/status`, {
          status: 'manager_review',
          note: 'Ses dosyası değiştirildi',
          updateAnnouncementStatus: false
        });
      }

      await queryClient.refetchQueries({ queryKey: ["announcement", id] });
      toast.success("Ses dosyası başarıyla değiştirildi", { id: toastId });
    } catch (error) {
      handleError(error, "Ses dosyası değiştirilirken hata oluştu");
    } finally {
      setIsUploading(false);
      setReplacingAudioId(null);
      e.target.value = '';
    }
  };

  if (isLoading || !announcement) {
    return (
      <div className="h-full flex-1">
        <div className="flex-1 space-y-6 p-8 pt-6">
          <Skeleton className="h-56 w-full rounded-2xl" />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <Skeleton className="h-[200px] w-full" />
              <Skeleton className="h-[200px] w-full" />
            </div>
            <Skeleton className="h-[400px] w-full" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div ref={pageTopRef} className="h-full flex-1">
      <div className="flex-1 space-y-6 p-8 pt-6">
        {/* Hero Section */}
        <div className="rounded-2xl overflow-hidden shadow-lg">
          <div
            className="flex"
            style={{ background: 'linear-gradient(to right, #0891b2, #06b6d4)' }}
          >
            <div className="p-8 flex justify-between items-start relative w-full">
              <div className="space-y-3 flex-1">
                <button
                  onClick={() => navigate("/announcements")}
                  className="inline-flex items-center gap-2 text-sm font-medium bg-transparent text-white/80 hover:text-white hover:bg-white/10 rounded-md px-3 py-2 -ml-2 transition-colors border-none outline-none"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Geri
                </button>
                <h1 className="text-3xl font-bold text-white">
                  {announcement.title}
                </h1>
                <p className="text-white/90 text-sm max-w-xl leading-relaxed">
                  Anons içeriğini düzenleyin, ses dosyalarını yükleyin veya değiştirin.
                </p>
              </div>
              <div
                className="w-40 h-40 rounded-lg overflow-hidden relative z-10 flex items-center justify-center ml-8"
                style={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
              >
                <Megaphone className="w-20 h-20 text-white" strokeWidth={1.5} />
              </div>
            </div>
          </div>
        </div>

        {/* Durum ve Aksiyonlar */}
        <div className="flex items-center justify-between bg-white rounded-xl border border-slate-200 shadow-sm p-4">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-slate-600">Durum:</span>
            <Badge className={cn("px-3 py-1.5", statusColors[getEffectiveStatusKey(announcement.status, announcement.audioFile)])}>
              {getEffectiveStatusLabel(announcement.status, announcement.audioFile, announcement.statusHistory)}
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleStatusChange('approved')}
              disabled={isStatusUpdating || announcement.status === 'approved'}
              className="bg-green-50 text-green-700 border-green-300 hover:bg-green-100"
            >
              <Check className="h-4 w-4 mr-2" />
              Onayla
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleStatusChange('pending_admin')}
              disabled={isStatusUpdating || announcement.status === 'pending_admin'}
              className="bg-orange-50 text-orange-700 border-orange-300 hover:bg-orange-100"
            >
              <AlertTriangle className="h-4 w-4 mr-2" />
              Düzeltme İste
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleStatusChange('rejected')}
              disabled={isStatusUpdating || announcement.status === 'rejected'}
              className="bg-red-50 text-red-700 border-red-300 hover:bg-red-100"
            >
              <X className="h-4 w-4 mr-2" />
              Reddet
            </Button>
            <Button
              size="sm"
              onClick={() => handleStatusChange('manager_review')}
              disabled={isStatusUpdating || announcement.status === 'manager_review'}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <ArrowRight className="h-4 w-4 mr-2" />
              Manager'a Gönder
            </Button>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Sol Panel - Form Alanları */}
            <div className="lg:col-span-2 space-y-6">
              {/* Anons Bilgileri - Sadece Görüntüleme */}
              <Card className="overflow-hidden border border-slate-200 shadow-sm bg-white rounded-xl">
                <CardHeader className="bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200">
                  <CardTitle className="text-base font-semibold text-slate-800">Anons Bilgileri</CardTitle>
                  <CardDescription>Manager tarafından gönderilen anons içeriği</CardDescription>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700">Başlık</Label>
                    <div className="h-11 px-3 py-2 rounded-md border border-slate-200 bg-slate-50 text-slate-700 flex items-center">
                      {announcement.title || '-'}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700">İçerik</Label>
                    <div className="min-h-[120px] px-3 py-2 rounded-md border border-slate-200 bg-slate-50 text-slate-700 whitespace-pre-wrap">
                      {announcement.content || '-'}
                    </div>
                  </div>
                  {/* Müşteriye Not İlet - hazır notlardan birini seçip müşteriye gönder */}
                  {(() => {
                    const sentNotes = (announcement.statusHistory || [])
                      .filter((h: StatusHistory) => h.to === 'pending_admin' && h.note?.trim())
                      .sort((a: StatusHistory, b: StatusHistory) => new Date(b.date).getTime() - new Date(a.date).getTime());
                    const limitReached = sentNotes.length >= MAX_QUICK_NOTES;

                    return (
                      <div className="space-y-2 pt-2 border-t border-slate-200">
                        <Label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                          <MessageSquare className="h-4 w-4" />
                          Müşteriye Not İlet
                        </Label>
                        {limitReached ? (
                          <div className="text-sm text-slate-500 bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
                            Not gönderme limitine ulaşıldı ({sentNotes.length}/{MAX_QUICK_NOTES})
                          </div>
                        ) : (
                          <>
                            <div className="space-y-1.5">
                              {QUICK_NOTE_OPTIONS.map((note) => (
                                <button
                                  key={note}
                                  type="button"
                                  onClick={() => setSelectedQuickNote(note)}
                                  className={cn(
                                    "w-full text-left text-sm px-3 py-2 rounded-md border transition-colors",
                                    selectedQuickNote === note
                                      ? "border-blue-400 bg-blue-50 text-blue-800"
                                      : "border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100"
                                  )}
                                >
                                  {note}
                                </button>
                              ))}
                            </div>
                            <Button
                              type="button"
                              size="sm"
                              onClick={handleSendQuickNote}
                              disabled={!selectedQuickNote || isSendingQuickNote}
                              className="mt-1"
                            >
                              {isSendingQuickNote ? "Gönderiliyor..." : `Gönder (${sentNotes.length}/${MAX_QUICK_NOTES})`}
                            </Button>
                          </>
                        )}
                        {/* Gönderilmiş notların geçmişi - tarih/saat ile listelenir */}
                        {sentNotes.length > 0 && (
                          <div className="space-y-1.5 pt-2">
                            <div className="text-xs font-medium text-slate-500 uppercase tracking-wide">Gönderilen Notlar</div>
                            {sentNotes.map((h: StatusHistory) => (
                              <div key={h._id} className="text-sm bg-amber-50 border border-amber-100 rounded-md px-3 py-2">
                                <div className="text-xs text-slate-500 mb-0.5">{formatShortDate(h.date)}</div>
                                <div className="text-slate-700">{h.note}</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  {/* Ekli Dosyalar */}
                  {announcement.attachments && announcement.attachments.length > 0 && (
                    <div className="space-y-2 pt-2 border-t border-slate-200">
                      <Label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                        <Paperclip className="h-4 w-4" />
                        Ekli Dosyalar
                      </Label>
                      <div className="space-y-2">
                        {announcement.attachments.map((attachment: any, index: number) => (
                          <div key={attachment._id || index} className="flex items-center justify-between p-3 rounded-md border border-slate-200 bg-slate-50">
                            <div className="flex items-center gap-3">
                              <div className="p-1.5 bg-blue-100 rounded">
                                <File className="h-4 w-4 text-blue-600" />
                              </div>
                              <div>
                                <div className="font-medium text-slate-900 text-sm">
                                  {attachment.fileName || `Dosya ${index + 1}`}
                                </div>
                                <div className="text-xs text-slate-500 capitalize">
                                  {attachment.fileType || 'dosya'}
                                </div>
                              </div>
                            </div>
                            <a
                              href={attachment.filePath}
                              download={attachment.fileName || 'dosya'}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-slate-200 transition-colors"
                            >
                              <Download className="h-4 w-4 text-slate-600" />
                            </a>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Ses Dosyaları */}
              <Card ref={audioSectionRef} className="overflow-hidden border border-slate-200 shadow-sm bg-white rounded-xl">
                <CardHeader className="bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
                        <FileAudio className="h-4 w-4" />
                        Ses Dosyaları
                      </CardTitle>
                      <CardDescription className="mt-1">
                        {hasAudioFiles(announcement.audioFile)
                          ? "Yüklenen ses dosyaları"
                          : "Henüz ses dosyası yüklenmemiş"}
                      </CardDescription>
                    </div>
                    {Array.isArray(announcement.audioFile) && announcement.audioFile.length > 1 && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={isDeletingAll}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                        onClick={async () => {
                          try {
                            setIsDeletingAll(true);
                            const audioFiles = announcement.audioFile as AudioFile[];
                            for (const audio of audioFiles) {
                              await axios.delete(`/announcements/${id}/audio/${audio._id}`);
                            }
                            await queryClient.refetchQueries({ queryKey: ["announcement", id] });
                            toast.success("Tüm ses dosyaları silindi");
                          } catch (error) {
                            handleError(error, "Ses dosyaları silinirken hata oluştu");
                          } finally {
                            setIsDeletingAll(false);
                          }
                        }}
                      >
                        {isDeletingAll ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                            Siliniyor...
                          </>
                        ) : (
                          <>
                            <Trash2 className="h-4 w-4 mr-1" />
                            Tümünü Sil
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  {hasAudioFiles(announcement.audioFile) ? (
                    <div className="space-y-4">
                      {Array.isArray(announcement.audioFile) ? (
                        announcement.audioFile.map((audio: AudioFile, index: number) => (
                          <div key={index} className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                            <div className="flex items-center gap-3 mb-3">
                              <div className="flex-1">
                                <span className="font-medium text-slate-900">{audio.fileName || `Ses Dosyası ${index + 1}`}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge className={cn("text-xs", {
                                  "bg-green-100 text-green-700": audio.status === 'approved',
                                  "bg-yellow-100 text-yellow-700": audio.status === 'pending_admin',
                                  "bg-blue-100 text-blue-700": audio.status === 'manager_review',
                                  "bg-red-100 text-red-700": audio.status === 'rejected'
                                })}>
                                  {audio.status === 'approved' ? "Onaylandı" :
                                   audio.status === 'pending_admin' ? "Revize Bekleniyor" :
                                   audio.status === 'rejected' ? "Reddedildi" : "İncelemede"}
                                </Badge>
                                {/* Değiştir Butonu - Revize Bekleniyor durumunda göster */}
                                {audio.status === 'pending_admin' && (
                                  <>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                      onClick={() => document.getElementById(`replaceAudio-${audio._id}`)?.click()}
                                      disabled={replacingAudioId === audio._id}
                                    >
                                      <RefreshCw className={cn("h-4 w-4", replacingAudioId === audio._id && "animate-spin")} />
                                    </Button>
                                    <input
                                      type="file"
                                      id={`replaceAudio-${audio._id}`}
                                      className="hidden"
                                      accept="audio/*"
                                      onChange={(e) => handleReplaceAudio(e, audio._id)}
                                    />
                                  </>
                                )}
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => window.open(audio.url, '_blank')}
                                >
                                  <Download className="h-4 w-4" />
                                </Button>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-600 hover:text-red-700"
                                  onClick={() => {
                                    axios.delete(`/announcements/${id}/audio/${audio._id}`)
                                      .then(() => {
                                        queryClient.invalidateQueries({ queryKey: ["announcement", id] });
                                        toast.success("Ses dosyası silindi");
                                      })
                                      .catch((error) => handleError(error, "Silme hatası"));
                                  }}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                            <CustomAudioPlayer src={audio.url} />
                            {/* Revize Notu - statusHistory'den */}
                            {audio.statusHistory && audio.statusHistory.length > 0 && audio.statusHistory.some((h: AudioStatusHistory) => h.note && h.note.trim()) && (
                              <div className="mt-3 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                                <div className="flex items-center gap-2 mb-2">
                                  <MessageSquare className="h-4 w-4 text-orange-600" />
                                  <span className="text-sm font-medium text-orange-700">Revize Notu</span>
                                </div>
                                {audio.statusHistory
                                  .filter((h: AudioStatusHistory) => h.note && h.note.trim())
                                  .map((history: AudioStatusHistory) => (
                                    <div key={history._id} className="text-sm text-slate-700">
                                      <p className="whitespace-pre-wrap">{history.note}</p>
                                      <div className="flex items-center gap-2 mt-1 text-xs text-orange-600">
                                        <UserCircle className="h-3 w-3" />
                                        <span>{history.user?.name || 'Bilinmeyen'}</span>
                                        <span>•</span>
                                        <span>{formatShortDate(history.date)}</span>
                                      </div>
                                    </div>
                                  ))}
                              </div>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="flex-1">
                              <div className="font-medium text-slate-900">
                                {typeof announcement.audioFile === 'string'
                                  ? announcement.audioFile.split('/').pop()
                                  : announcement.audioFile.fileName || 'Ses Dosyası'}
                              </div>
                            </div>
                          </div>
                          <CustomAudioPlayer
                            src={typeof announcement.audioFile === 'string' ? announcement.audioFile : announcement.audioFile.url}
                          />
                        </div>
                      )}
                    </div>
                  ) : isUploading ? (
                    <div className="flex flex-col items-center justify-center border-2 border-dashed border-purple-300 rounded-lg p-8 bg-purple-50">
                      <p className="text-sm font-medium text-purple-700 mb-3">Yükleniyor...</p>
                      <div className="w-full max-w-xs">
                        <div className="h-3 bg-purple-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-purple-500 transition-all duration-300 ease-out"
                            style={{ width: `${uploadProgress}%` }}
                          />
                        </div>
                        <p className="text-xs text-purple-600 text-center mt-2">%{uploadProgress}</p>
                      </div>
                    </div>
                  ) : (
                    <div
                      className="flex flex-col items-center justify-center border-2 border-dashed border-slate-200 rounded-lg p-8 cursor-pointer hover:border-purple-400 transition-colors"
                      onClick={() => document.getElementById('audioInput')?.click()}
                    >
                      <Upload className="h-8 w-8 text-slate-400 mb-2" />
                      <p className="text-sm font-medium text-slate-600">Ses dosyası yüklemek için tıklayın</p>
                      <p className="text-xs text-slate-500 mt-1">veya dosyayı sürükleyip bırakın</p>
                      <input
                        type="file"
                        id="audioInput"
                        className="hidden"
                        accept="audio/*"
                        multiple
                        onChange={(e) => {
                          const files = e.target.files;
                          if (files && files.length > 0) handleAudioUpload(files);
                          e.target.value = '';
                        }}
                      />
                    </div>
                  )}
                </CardContent>
              </Card>

            </div>

            {/* Sağ Panel - Durum ve Hedefler */}
            <div className="space-y-4 sticky top-6 self-start">
              <Card className="overflow-hidden border border-slate-200 shadow-sm bg-white rounded-xl">
                <CardHeader className="bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200">
                  <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Durum Bilgisi
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Oluşturulma</span>
                    <span className="text-slate-700">{formatShortDate(announcement.createdAt)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Son Güncelleme</span>
                    <span className="text-slate-700">{formatShortDate(announcement.updatedAt)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Oluşturan</span>
                    <span className="text-slate-700">{announcement.createdBy?.name || '-'}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Zamanlama Ayarları */}
              <Card className="overflow-hidden border border-slate-200 shadow-sm bg-white rounded-xl">
                <CardHeader className="bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200 py-3">
                  <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
                    <Settings className="h-4 w-4" />
                    Zamanlama
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-3 space-y-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium text-slate-500">Tip</Label>
                    <Select
                      value={formData.scheduleType || announcement.scheduleType}
                      onValueChange={handleScheduleTypeChange}
                    >
                      <SelectTrigger className="h-9 border-slate-200 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="songs">Şarkı Bazlı</SelectItem>
                        <SelectItem value="minutes">Dakika Bazlı</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {(formData.scheduleType || announcement.scheduleType) === 'songs' ? (
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium text-slate-500">Şarkı Aralığı</Label>
                      <Input
                        type="number"
                        name="songInterval"
                        value={formData.songInterval !== undefined ? formData.songInterval : announcement.songInterval || ''}
                        onChange={handleChange}
                        className="h-9 border-slate-200 text-sm"
                        placeholder="Kaç şarkıda bir"
                      />
                    </div>
                  ) : (
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium text-slate-500">Dakika Aralığı</Label>
                      <Input
                        type="number"
                        name="minuteInterval"
                        value={formData.minuteInterval !== undefined ? formData.minuteInterval : announcement.minuteInterval || ''}
                        onChange={handleChange}
                        className="h-9 border-slate-200 text-sm"
                        placeholder="Kaç dakikada bir"
                      />
                    </div>
                  )}
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium text-slate-500">Başlangıç</Label>
                    <Input
                      type="datetime-local"
                      name="startDate"
                      value={formatDateForInput(formData.startDate || announcement.startDate)}
                      onChange={handleChange}
                      className="h-9 border-slate-200 text-sm"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium text-slate-500">Bitiş</Label>
                    <Input
                      type="datetime-local"
                      name="endDate"
                      value={formatDateForInput(formData.endDate || announcement.endDate)}
                      onChange={handleChange}
                      className="h-9 border-slate-200 text-sm"
                    />
                  </div>
                  <div className="flex items-center space-x-2 pt-1">
                    <Checkbox
                      id="immediateInterrupt"
                      checked={formData.immediateInterrupt ?? announcement.immediateInterrupt ?? false}
                      onChange={handleCheckboxChange}
                    />
                    <Label htmlFor="immediateInterrupt" className="text-xs font-normal text-slate-600">
                      Acil Kesme
                    </Label>
                  </div>
                </CardContent>
              </Card>

              {/* Hedef Özeti */}
              <Card className="overflow-hidden border border-slate-200 shadow-sm bg-white rounded-xl">
                <CardHeader className="bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200">
                  <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
                    <Target className="h-4 w-4" />
                    Hedef Özeti
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  {announcement.targetDevices?.length === 0 && announcement.targetGroups?.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-4">Hedef seçilmemiş</p>
                  ) : (
                    <div className="space-y-3">
                      {/* Grup Sayısı */}
                      {announcement.targetGroups?.length > 0 && (
                        <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                              <Target className="h-4 w-4 text-purple-600" />
                            </div>
                            <span className="text-sm font-medium text-slate-700">Gruplar</span>
                          </div>
                          <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100">
                            {announcement.targetGroups.length} grup
                          </Badge>
                        </div>
                      )}
                      {/* Cihaz Sayısı */}
                      {announcement.targetDevices?.length > 0 && (
                        <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                              <Radio className="h-4 w-4 text-blue-600" />
                            </div>
                            <span className="text-sm font-medium text-slate-700">Cihazlar</span>
                          </div>
                          <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">
                            {announcement.targetDevices.length} cihaz
                          </Badge>
                        </div>
                      )}
                      {/* Online/Offline Durumu */}
                      {announcement.targetDevices?.length > 0 && (
                        <div className="flex items-center gap-4 pt-2 border-t border-slate-100">
                          <div className="flex items-center gap-1.5 text-xs text-slate-600">
                            <div className="w-2 h-2 rounded-full bg-green-500" />
                            <span>{announcement.targetDevices.filter((d: Device) => d.isOnline).length} çevrimiçi</span>
                          </div>
                          <div className="flex items-center gap-1.5 text-xs text-slate-600">
                            <div className="w-2 h-2 rounded-full bg-slate-300" />
                            <span>{announcement.targetDevices.filter((d: Device) => !d.isOnline).length} çevrimdışı</span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Durum Geçmişi */}
              {announcement.statusHistory && announcement.statusHistory.length > 0 && (
                <Card className="overflow-hidden border border-slate-200 shadow-sm bg-white rounded-xl">
                  <CardHeader className="bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200 py-3">
                    <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
                      <HistoryIcon className="h-4 w-4" />
                      Durum Geçmişi
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-3">
                    <ScrollArea className="h-[180px]">
                      <div className="space-y-2 pr-2">
                        {announcement.statusHistory.slice(0, 5).map((history: StatusHistory) => (
                          <div key={history._id} className="p-2 border border-slate-200 rounded-lg bg-slate-50">
                            <div className="flex items-center justify-between mb-1">
                              <Badge variant="outline" className={cn("text-[10px] px-1.5 py-0", {
                                "bg-green-50 text-green-700 border-green-300": history.to === 'approved',
                                "bg-yellow-50 text-yellow-700 border-yellow-300": history.to === 'pending_admin',
                                "bg-blue-50 text-blue-700 border-blue-300": history.to === 'manager_review',
                                "bg-red-50 text-red-700 border-red-300": history.to === 'rejected'
                              })}>
                                {history.to === 'approved' ? "Onaylandı" :
                                 history.to === 'pending_admin' ? "Revize" :
                                 history.to === 'manager_review' ? "İnceleme" :
                                 history.to === 'rejected' ? "Reddedildi" : history.to}
                              </Badge>
                              <span className="text-[10px] text-slate-500">{formatShortDate(history.date)}</span>
                            </div>
                            <div className="flex items-center gap-1 text-[10px] text-slate-600">
                              <UserCircle className="h-3 w-3" />
                              <span>{history.user.name}</span>
                            </div>
                          </div>
                        ))}
                        {announcement.statusHistory.length > 5 && (
                          <p className="text-xs text-slate-500 text-center py-1">
                            +{announcement.statusHistory.length - 5} kayıt daha
                          </p>
                        )}
                      </div>
                    </ScrollArea>
                  </CardContent>
                </Card>
              )}


              {/* Kaydet Butonu */}
              <Button
                type="submit"
                disabled={isSaving}
                className="w-full h-12 text-base bg-blue-600 hover:bg-blue-700"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Kaydediliyor...
                  </>
                ) : (
                  <>
                    <Save className="h-5 w-5 mr-2" />
                    Değişiklikleri Kaydet
                  </>
                )}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
